import { Box, xcss } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import { SeamlessTextArea } from '@databases/form/FormComponents';
import { Metrics } from '@databases/frontend-metrics';
import { useTypingPerformance } from '@databases/frontend-metrics/useTypingPerformance';
import { getTextDelta, useTextDelta } from '@databases/react/useTextDelta';
import { defaultStoreUtils } from '@databases/redux';
import { selectFieldIdsByContentId } from '@databases/redux/database-view';
import { DatabaseFieldType } from '@databases/types/database';
import composeRefs from '@seznam/compose-react-refs';
import { type Op } from 'quill-delta';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import { type TextLikeFieldType } from '../types.ts';

import {
  MAX_POPOVER_EDIT_WIDTH,
  TEXT_VALUE_MAX_TEXT_AREA_HEIGHT,
  TEXT_VALUE_MIN_TEXT_AREA_HEIGHT,
  useAdjustTextAreaDimensions
} from './popover-edit-common/index.ts';
import { createTextDeltaChange } from './text-value-change.ts';
import { type EditComponentProps } from './types.ts';

const textAreaContainerStyling = xcss({
  display: 'flex',
  flexDirection: 'row',
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 'dialog' // Ensure container sits above blue dots
});

const lastFieldStylings = xcss({
  flexDirection: 'row-reverse'
});

function TextValuePopoverEdit<Type extends TextLikeFieldType>({
  field,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  isDefaultValue,
  isSeamless,
  isRenderedInSidePanel,
  isRenderedInContextualFieldMenu,
  contentId,
  isBulkEdit,
  ariaLabel,
  initialKey,
  placeholder,
  'aria-describedby': ariaDescribedBy
}: EditComponentProps<Type>) {
  const maxWidthReachedRef = useRef<boolean>(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState(initialKey ?? value.plain);
  const [isFocused, setIsFocused] = useState(false);
  const store = defaultStoreUtils.useStore();
  const isContextualMenusM2Enabled = getExperimentValue(
    'databasesContextualMenusM2',
    'enabled',
    { fireExperimentExposure: false }
  );
  const isCompactDefaultValueInput =
    Boolean(isDefaultValue) && Boolean(isRenderedInContextualFieldMenu);

  const adjustTextAreaDimensions = useAdjustTextAreaDimensions({
    textAreaRef,
    maxWidthReachedRef,
    maxPopoverEditWidth: isRenderedInSidePanel
      ? textAreaRef.current?.scrollWidth
      : MAX_POPOVER_EDIT_WIDTH,
    minTextAreaHeight: isCompactDefaultValueInput
      ? TEXT_VALUE_MIN_TEXT_AREA_HEIGHT
      : undefined,
    shouldExpandFromCell: !isCompactDefaultValueInput
  });

  useEffect(() => {
    adjustTextAreaDimensions();
  }, [inputValue, adjustTextAreaDimensions]);

  useEffect(() => {
    if (initialKey !== undefined) {
      const oldLength = value.plain.length;
      const delta: Op[] =
        oldLength > 0
          ? [{ delete: oldLength }, { insert: initialKey }]
          : [{ insert: initialKey }];
      onChange({ plain: initialKey }, createTextDeltaChange(field.type, delta));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldIds = selectFieldIdsByContentId(store.getState(), {
    contentId
  });

  const isLastField = fieldIds.indexOf(field.id) === fieldIds.length - 1;

  const Component = isSeamless ? SeamlessTextArea : TextArea;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = event.currentTarget.value;
      const delta = getTextDelta(
        inputValue,
        newValue,
        event.currentTarget.selectionStart
      );
      setInputValue(newValue);
      if (delta.length > 0) {
        onChange(
          { plain: newValue },
          {
            type: DatabaseFieldType.TEXT,
            delta
          }
        );
      }
    },
    [inputValue, onChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        isRenderedInContextualFieldMenu &&
        (event.key === 'Backspace' || event.key === 'Delete')
      ) {
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation?.();
      }
    },
    [isRenderedInContextualFieldMenu]
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(true);
      // Put cursor to the end of the field
      e.currentTarget.setSelectionRange(
        e.currentTarget.value.length,
        e.currentTarget.value.length
      );

      adjustTextAreaDimensions();
      onFocus?.(e);
    },
    [onFocus, adjustTextAreaDimensions]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  useTextDelta(
    textAreaRef.current,
    useCallback(
      (delta: Op[], newValue: string) => {
        if (isRenderedInContextualFieldMenu) {
          return;
        }
        setInputValue(newValue);
        onChange({ plain: newValue }, createTextDeltaChange(field.type, delta));
      },
      [field.type, isRenderedInContextualFieldMenu, onChange]
    )
  );

  const typingPerformanceRef = useTypingPerformance<HTMLTextAreaElement>(
    Metrics.MEAN_TEXT_VALUE_TYPING
  );
  return (
    <Box
      testId={`text-value-edit-expand-${isLastField ? 'left' : 'right'}`}
      xcss={[textAreaContainerStyling, isLastField && lastFieldStylings]}
    >
      <Component
        value={inputValue}
        onChange={isRenderedInContextualFieldMenu ? handleChange : undefined}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus={autoFocus}
        placeholder={placeholder}
        resize="none"
        wrap="off"
        minimumRows={isCompactDefaultValueInput ? 1 : undefined}
        ref={composeRefs(textAreaRef, typingPerformanceRef)}
        testId={'text-value-edit'}
        aria-describedby={ariaDescribedBy}
        style={{
          border: isCompactDefaultValueInput
            ? `${token('border.width', '1px')} solid ${
                isFocused ? token('color.border.focused') : token('color.border')
              }`
            : `${token('border.width.selected', '2px')} solid ${token(
                'color.background.accent.blue.subtle',
                '#579DFF'
              )}`,
          borderRadius: isCompactDefaultValueInput
            ? isContextualMenusM2Enabled
              ? token('radius.medium', '6px')
              : token('radius.medium')
            : 'radius.small',
          ...(isCompactDefaultValueInput && {
            background: token('color.background.input'),
            boxShadow:
              isContextualMenusM2Enabled && isFocused
                ? `inset 0 0 0 ${token('border.width', '1px')} ${token(
                    'color.border.focused',
                    '#388BFF'
                  )}`
                : 'none',
            outline: 'none',
            paddingBlock: token('space.050', '4px'),
            height: `${TEXT_VALUE_MIN_TEXT_AREA_HEIGHT}px`,
            minHeight: `${TEXT_VALUE_MIN_TEXT_AREA_HEIGHT}px`
          }),
          overflow: 'hidden',
          position:
            isCompactDefaultValueInput || isRenderedInSidePanel || isBulkEdit
              ? 'relative'
              : 'absolute',
          boxSizing: 'border-box',
          maxHeight: `${TEXT_VALUE_MAX_TEXT_AREA_HEIGHT}px`,
          // Override @atlaskit/textarea's font.body.large to match the cell's 14px font size.
          font: token('font.body', '14px')
        }}
        aria-label={ariaLabel}
      />
    </Box>
  );
}

export default TextValuePopoverEdit;
